<template>
  <footer class="public-footer">
    <div class="public-footer__glow" aria-hidden="true" />

    <VContainer class="public-footer__main py-12 py-md-14">
      <VRow class="ga-y-8">
        <!-- Brand -->
        <VCol cols="12" lg="4">
          <div class="d-flex align-center ga-3 mb-5">
            <VAvatar size="56" rounded="lg" class="public-footer__logo-wrap">
              <VImg :src="logo" alt="TVET MIS logo" cover />
            </VAvatar>
            <div>
              <div class="public-footer__brand">TVET MIS</div>
              <div class="public-footer__tagline">Somalia · MoLSA</div>
            </div>
          </div>
          <p class="public-footer__desc mb-6">
            National management information system for technical and vocational
            education and training - connecting institutions, trainers and
            learners across Somalia.
          </p>
          <div class="d-flex flex-wrap ga-2">
            <VBtn
              variant="outlined"
              color="white"
              rounded="lg"
              size="small"
              class="public-footer__cta"
              to="/login"
            >
              <VIcon start size="18">mdi-login</VIcon>
              Sign in
            </VBtn>
            <VBtn
              variant="outlined"
              color="white"
              rounded="lg"
              size="small"
              class="public-footer__cta"
              to="/tvet-institutions"
            >
              <VIcon start size="18">mdi-domain</VIcon>
              Browse institutions
            </VBtn>
            <VBtn
              variant="outlined"
              color="white"
              rounded="lg"
              size="small"
              class="public-footer__cta"
              to="/tvet-trainers"
            >
              <VIcon start size="18">mdi-human-male-board-poll</VIcon>
              Browse trainers
            </VBtn>
          </div>
        </VCol>

        <!-- Quick links -->
        <VCol cols="12" sm="6" lg="2">
          <h3 class="public-footer__heading">Explore</h3>
          <nav class="public-footer__nav" aria-label="Footer navigation">
            <RouterLink
              v-for="link in quickLinks"
              :key="link.to"
              :to="link.to"
              class="public-footer__link"
            >
              <VIcon :icon="link.icon" size="18" class="me-2 opacity-70" />
              {{ link.label }}
            </RouterLink>
          </nav>
        </VCol>

        <!-- Partners -->
        <VCol cols="12" sm="6" lg="3">
          <h3 class="public-footer__heading">Development partners</h3>
          <p class="public-footer__muted mb-4">
            Supported by international partners investing in Somalia’s TVET
            sector.
          </p>
          <div class="public-footer__partners">
            <a
              v-for="partner in partners"
              :key="partner.name"
              :href="partner.url"
              target="_blank"
              rel="noopener noreferrer"
              class="public-footer__partner"
              :title="`Visit ${partner.name}`"
            >
              <span class="public-footer__partner-logo">
                <img
                  :src="partner.logo"
                  :alt="`${partner.name} logo`"
                  class="public-footer__partner-img"
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <span class="public-footer__partner-text">
                <span class="public-footer__partner-name">{{
                  partner.shortName
                }}</span>
                <span class="public-footer__partner-full">{{
                  partner.name
                }}</span>
              </span>
              <VIcon
                icon="mdi-arrow-top-right"
                size="16"
                class="public-footer__partner-icon"
                aria-hidden="true"
              />
            </a>
          </div>
        </VCol>

        <!-- Contact -->
        <VCol cols="12" lg="3">
          <h3 class="public-footer__heading">Contact MoLSA</h3>
          <ul class="public-footer__contact-list">
            <li v-for="item in contactItems" :key="item.label">
              <component
                :is="item.href ? 'a' : 'div'"
                :href="item.href"
                class="public-footer__contact-item"
                :class="{ 'public-footer__contact-item--link': item.href }"
              >
                <span class="public-footer__contact-icon">
                  <VIcon :icon="item.icon" size="20" />
                </span>
                <span>
                  <span class="public-footer__contact-label">{{
                    item.label
                  }}</span>
                  <span class="public-footer__contact-value">{{
                    item.value
                  }}</span>
                </span>
              </component>
            </li>
          </ul>
        </VCol>
      </VRow>
    </VContainer>

    <div class="public-footer__bar">
      <VContainer class="py-4 py-md-5">
        <div
          class="d-flex flex-column flex-sm-row align-center justify-space-between ga-3 text-center text-sm-start"
        >
          <p class="public-footer__copyright mb-0">
            &copy; {{ currentYear }} TVET Management Information System. All
            rights reserved.
          </p>
          <p class="public-footer__copyright mb-0 opacity-80">
            Ministry of Labour and Social Affairs · Federal Republic of Somalia
          </p>
        </div>
      </VContainer>
    </div>
  </footer>
</template>

<script setup>
import logo from "@/assets/images/logo.svg";
import afdbLogo from "@/assets/images/footer/afdb.png";
import gizLogo from "@/assets/images/footer/giz.png";
import jicaLogo from "@/assets/images/footer/jica.png";

const currentYear = new Date().getFullYear();

const quickLinks = [
  {
    label: "Institutions directory",
    to: "/tvet-institutions",
    icon: "mdi-domain",
  },
  {
    label: "Trainers directory",
    to: "/tvet-trainers",
    icon: "mdi-human-male-board-poll",
  },
  {
    label: "Online applications",
    to: "/online-applications",
    icon: "mdi-laptop",
  },
  { label: "Home", to: "/", icon: "mdi-home-outline" },
];

const partners = [
  {
    name: "African Development Bank",
    shortName: "AfDB",
    logo: afdbLogo,
    url: "https://www.afdb.org",
  },
  {
    name: "Deutsche Gesellschaft für Internationale Zusammenarbeit",
    shortName: "GIZ",
    logo: gizLogo,
    url: "https://www.giz.de/en",
  },
  {
    name: "Japan International Cooperation Agency",
    shortName: "JICA",
    logo: jicaLogo,
    url: "https://www.jica.go.jp/english/",
  },
];

const contactItems = [
  {
    icon: "mdi-domain",
    label: "Ministry",
    value: "Ministry of Labour and Social Affairs (MoLSA)",
  },
  {
    icon: "mdi-email-outline",
    label: "General enquiries",
    value: "info@tvetmis.gov.so",
    href: "mailto:info@tvetmis.gov.so",
  },
  // {
  //   icon: "mdi-email-variant",
  //   label: "Director General",
  //   value: "dg@molsa.gov.so",
  //   href: "mailto:dg@molsa.gov.so",
  // },
  // {
  //   icon: "mdi-phone-outline",
  //   label: "Phone",
  //   value: "+252 619 636 308",
  //   href: "tel:+252619636308",
  // },
  {
    icon: "mdi-map-marker-outline",
    label: "Address",
    value: "Jubba Road, Shangani, Mogadishu, Somalia",
  },
];
</script>

<style scoped>
.public-footer {
  position: relative;
  margin-top: auto;
  background: rgb(var(--v-theme-primary));
  color: rgba(255, 255, 255, 0.92);
  font-family: "Public Sans", system-ui, -apple-system, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow: hidden;
  margin-top: 40px;
}

.public-footer__glow {
  position: absolute;
  top: -120px;
  right: -80px;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background: transparent;
  pointer-events: none;
}

.public-footer__main {
  position: relative;
  z-index: 1;
}

.public-footer__logo-wrap {
  background: rgba(255, 255, 255, 0.95);
  padding: 6px;
}

.public-footer__brand {
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: #fff;
}

.public-footer__tagline {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 2px;
}

.public-footer__desc {
  font-size: 0.9375rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.72);
  max-width: 22rem;
  margin: 0;
}

.public-footer__cta {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.public-footer__heading {
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 1.25rem;
}

.public-footer__muted {
  font-size: 0.875rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

.public-footer__nav {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.public-footer__link {
  display: inline-flex;
  align-items: center;
  font-size: 0.9375rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.88);
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;
}

.public-footer__link:hover {
  color: #fff;
  transform: translateX(4px);
}

.public-footer__partners {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  width: fit-content;
  max-width: 100%;
}

.public-footer__partner {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  max-width: 100%;
  padding: 8px 12px 8px 8px;
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: background 0.22s ease, border-color 0.22s ease,
    transform 0.22s ease, box-shadow 0.22s ease;
}

.public-footer__partner:hover {
  background: rgba(255, 255, 255, 0.11);
  border-color: rgba(255, 255, 255, 0.22);
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22);
}

.public-footer__partner:focus-visible {
  outline: 2px solid rgba(165, 180, 252, 0.9);
  outline-offset: 2px;
}

.public-footer__partner-logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: 40px;
  padding: 4px 6px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
}

.public-footer__partner-img {
  display: block;
  width: auto;
  height: 32px;
  max-width: 88px;
  object-fit: contain;
  object-position: center;
}

.public-footer__partner-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.public-footer__partner-name {
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff;
  line-height: 1.2;
}

.public-footer__partner-full {
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.55);
  max-width: 11rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.public-footer__partner-icon {
  flex-shrink: 0;
  margin-left: 2px;
  opacity: 0.45;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.public-footer__partner:hover .public-footer__partner-icon {
  opacity: 0.9;
  transform: translate(2px, -2px);
}

.public-footer__contact-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.public-footer__contact-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.875rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
}

.public-footer__contact-item--link:hover .public-footer__contact-value {
  color: #a5b4fc;
  text-decoration: underline;
}

.public-footer__contact-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

.public-footer__contact-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 2px;
}

.public-footer__contact-value {
  display: block;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  transition: color 0.2s ease;
}

.public-footer__bar {
  position: relative;
  z-index: 1;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
}

.public-footer__copyright {
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.5;
}

@media (max-width: 600px) {
  .public-footer__desc {
    max-width: none;
  }

  .public-footer__partners {
    width: 100%;
  }

  .public-footer__partner {
    width: 100%;
  }

  .public-footer__partner-full {
    max-width: 9rem;
  }
}
</style>
